/**
 * Sanitize a raw input string into a partial decimal value while typing.
 *
 * Rules:
 * - Keeps only digits `0-9` and at most ONE decimal separator.
 * - Accepts both `.` and `,` as separators and normalizes the output to `.`.
 * - Cannot start with a separator: leading separators are dropped entirely
 *   (no auto-inserted `0`), so the first character is always a digit or empty.
 * - Only the first separator is kept; subsequent ones are removed.
 * - Every other character (letters, spaces, symbols, `-`) is stripped.
 * - A trailing separator is preserved so the user can keep typing decimals.
 * - Decimal places are not clamped.
 *
 * @example sanitizeDecimalInput('.5')      // '5'
 * @example sanitizeDecimalInput('1,2,3')   // '1.23'
 * @example sanitizeDecimalInput('12a.3')   // '12.3'
 * @example sanitizeDecimalInput('12.')     // '12.'
 */
export const sanitizeDecimalInput = (raw: string): string => {
  // Strip everything except digits and separators.
  const cleaned = raw.replace(/[^0-9.,]/g, '');

  let result = '';
  let hasSeparator = false;

  for (const char of cleaned) {
    if (char === '.' || char === ',') {
      // Drop leading separators and any separator after the first one.
      if (result.length === 0 || hasSeparator) continue;
      hasSeparator = true;
      result += '.';
    } else {
      result += char;
    }
  }

  return result;
};

/**
 * Produce a clean canonical decimal string suitable for submission/storage.
 *
 * Builds on {@link sanitizeDecimalInput} and additionally:
 * - Removes a trailing separator (`'12.'` -> `'12'`).
 * - Removes redundant leading zeros in the integer part, keeping one
 *   (`'007'` -> `'7'`, `'0'` -> `'0'`, `'0.5'` -> `'0.5'`).
 * - Returns `''` for empty/invalid input.
 *
 * @example normalizeDecimal('12.')   // '12'
 * @example normalizeDecimal('007')   // '7'
 * @example normalizeDecimal('0.5')   // '0.5'
 * @example normalizeDecimal('')      // ''
 */
export const normalizeDecimal = (value: string): string => {
  let result = sanitizeDecimalInput(value);

  // Remove a trailing separator.
  if (result.endsWith('.')) {
    result = result.slice(0, -1);
  }

  if (result === '') return '';

  // Strip redundant leading zeros from the integer part, keeping one.
  const [integerPart = '', decimalPart] = result.split('.');
  const normalizedInteger = integerPart.replace(/^0+(?=\d)/, '');

  return decimalPart !== undefined ? `${normalizedInteger}.${decimalPart}` : normalizedInteger;
};
